/**
 * Email Validation Utility for MyMoolah Admin Portal
 * 
 * Provides robust RFC 5322–aligned syntax validation, character limit enforcement,
 * and smart typo detection for common email provider and TLD errors.
 */

const LOCAL_PART_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/;
const DOMAIN_LABEL_REGEX = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;
const TLD_REGEX = /^[a-zA-Z]{2,24}$/;

export const TYPO_DOMAIN_MAP: Record<string, string> = {
  // Gmail typos
  'gmail.om': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.coml': 'gmail.com',
  'gmail.come': 'gmail.com',
  'gmail.cpm': 'gmail.com',
  'gmail.vom': 'gmail.com',
  'gmail.xom': 'gmail.com',
  'gmail.comm': 'gmail.com',
  'gmail.coom': 'gmail.com',
  'gmail.coza': 'gmail.com',
  'gmail.co.za': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmaik.com': 'gmail.com',
  'gmaul.com': 'gmail.com',
  'gemail.com': 'gmail.com',
  'gamil.co.za': 'gmail.com',
  'gmial.co.za': 'gmail.com',
  'gmai.co.za': 'gmail.com',

  // iCloud typos
  'icloid.com': 'icloud.com',
  'icoud.com': 'icloud.com',
  'iclud.com': 'icloud.com',
  'icould.com': 'icloud.com',
  'iclod.com': 'icloud.com',
  'icloud.om': 'icloud.com',
  'icloud.con': 'icloud.com',
  'icloud.cm': 'icloud.com',
  'icloud.coza': 'icloud.com',
  'icloud.co.za': 'icloud.com',

  // Yahoo typos
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yaho.co.za': 'yahoo.com',
  'yahoo.om': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'yahoo.cm': 'yahoo.com',
  'yahoo.coza': 'yahoo.com',
  'ymail.om': 'ymail.com',
  'ymail.con': 'ymail.com',

  // Hotmail / Outlook / Live / MSN typos
  'hotmial.com': 'hotmail.com',
  'hotmaill.com': 'hotmail.com',
  'hotmil.com': 'hotmail.com',
  'hotamail.com': 'hotmail.com',
  'hotmail.om': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'hotmail.coza': 'hotmail.com',
  'hotmail.co.za': 'hotmail.com',
  'outloo.com': 'outlook.com',
  'outlok.com': 'outlook.com',
  'outllok.com': 'outlook.com',
  'outlook.om': 'outlook.com',
  'outlook.con': 'outlook.com',
  'outlook.coza': 'outlook.com',
  'live.om': 'live.com',
  'live.con': 'live.com',
  'msn.om': 'msn.com',
  'msn.con': 'msn.com',

  // South African ISP typos
  'vodamail.com': 'vodamail.co.za',
  'vodamail.coza': 'vodamail.co.za',
  'vodamail.co.zaa': 'vodamail.co.za',
  'vodamail.om': 'vodamail.co.za',
  'vodmail.co.za': 'vodamail.co.za',
  'webmail.coza': 'webmail.co.za',
  'webmail.co.zaa': 'webmail.co.za',
  'webmail.om': 'webmail.co.za',
  'telkomsa.coza': 'telkomsa.co.za',
  'telkomsa.co.zaa': 'telkomsa.co.za',
  'telkomsa.om': 'telkomsa.co.za',
  'mweb.coza': 'mweb.co.za',
  'mweb.co.zaa': 'mweb.co.za',
  'mweb.om': 'mweb.co.za',
};

const BOGUS_TLDS = new Set([
  'con', 'comm', 'coom', 'cpm', 'vom', 'xom', 'coml', 'come', 'coza', 'zaa'
]);

export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
  normalized?: string;
  suggestion?: string;
}

export function validateEmail(email: string): EmailValidationResult {
  if (email === null || email === undefined) {
    return { isValid: false, error: 'Email is required' };
  }

  const trimmed = String(email).trim();
  if (!trimmed) {
    return { isValid: false, error: 'Email is required' };
  }

  if (trimmed.length > 254) {
    return { isValid: false, error: 'Email must be 254 characters or fewer' };
  }

  if (/\s/.test(trimmed)) {
    return { isValid: false, error: 'Email must not contain spaces' };
  }

  const atIndex = trimmed.indexOf('@');
  if (atIndex === -1) {
    return { isValid: false, error: 'Email must contain an @ symbol' };
  }

  if (trimmed.indexOf('@', atIndex + 1) !== -1) {
    return { isValid: false, error: 'Email must contain only one @ symbol' };
  }

  const localPart = trimmed.slice(0, atIndex);
  const domainPart = trimmed.slice(atIndex + 1).toLowerCase();

  if (!localPart) {
    return { isValid: false, error: 'Email username cannot be empty' };
  }

  if (localPart.length > 64) {
    return { isValid: false, error: 'Email username must be 64 characters or fewer' };
  }

  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return { isValid: false, error: 'Email username cannot start or end with a dot' };
  }

  if (localPart.includes('..')) {
    return { isValid: false, error: 'Email username cannot contain consecutive dots' };
  }

  if (!LOCAL_PART_REGEX.test(localPart)) {
    return { isValid: false, error: 'Email username contains invalid characters' };
  }

  if (!domainPart) {
    return { isValid: false, error: 'Email domain cannot be empty' };
  }

  if (domainPart.length > 253) {
    return { isValid: false, error: 'Email domain is too long' };
  }

  if (domainPart.startsWith('.') || domainPart.endsWith('.')) {
    return { isValid: false, error: 'Email domain cannot start or end with a dot' };
  }

  if (domainPart.includes('..')) {
    return { isValid: false, error: 'Email domain cannot contain consecutive dots' };
  }

  const domainLabels = domainPart.split('.');
  if (domainLabels.length < 2) {
    return { isValid: false, error: 'Email domain must include a top-level domain (e.g. .com or .co.za)' };
  }

  for (const label of domainLabels) {
    if (!label) {
      return { isValid: false, error: 'Email domain contains empty section' };
    }
    if (label.length > 63) {
      return { isValid: false, error: 'Email domain section exceeds 63 characters' };
    }
    if (!DOMAIN_LABEL_REGEX.test(label)) {
      return { isValid: false, error: 'Email domain contains invalid characters or misplaced hyphens' };
    }
  }

  const tld = domainLabels[domainLabels.length - 1];
  if (!TLD_REGEX.test(tld)) {
    return { isValid: false, error: `Invalid top-level domain .${tld}` };
  }

  if (TYPO_DOMAIN_MAP[domainPart]) {
    const correctedDomain = TYPO_DOMAIN_MAP[domainPart];
    const suggestion = `${localPart}@${correctedDomain}`;
    return {
      isValid: false,
      error: `Invalid email domain '@${domainPart}'. Did you mean '${suggestion}'?`,
      suggestion,
    };
  }

  if (BOGUS_TLDS.has(tld)) {
    return {
      isValid: false,
      error: `Invalid top-level domain .${tld}. Please check for typos.`,
    };
  }

  if (tld === 'om' && domainLabels.length === 2) {
    const suggestion = `${localPart}@${domainLabels[0]}.com`;
    return {
      isValid: false,
      error: `Invalid email domain '@${domainPart}'. Did you mean '${suggestion}'?`,
      suggestion,
    };
  }

  const normalized = `${localPart.toLowerCase()}@${domainPart}`;
  return {
    isValid: true,
    normalized,
  };
}

export function isValidEmail(email: string): boolean {
  return validateEmail(email).isValid;
}

export function normalizeEmail(email: string): string {
  const res = validateEmail(email);
  if (res.isValid && res.normalized) {
    return res.normalized;
  }
  return String(email || '').trim();
}

export function getEmailTypoSuggestion(email: string): string | null {
  const res = validateEmail(email);
  return res.suggestion || null;
}
